import { TLoadDependeciesParams } from '@df/catalog/model';
import { Product as CoreProduct, IProductData, IProductDataV2, IProductDataV3 } from '@df/catalog/product/product';
import { ProductResource } from '@df/catalog/product/product.resource';
import { NgInject } from '@df/ng/ng-inject';
import { CustomerModel } from '@df/session/api/customer.model';
import { optionImagePipe } from '@df/ui/common/option-image.pipe';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { TaxMode } from '@df/ui/price/price.service';
import { ICmsBlockResource } from '@icms/core/resource/block/icms-block.resource';
import { Nexus } from '@tomandco/nexus-api-types';
import sortBy from 'lodash-es/sortBy';
import { ECustomerGroup } from '../../checkout/module/trade/trade.service';
import { NCmsProductDeliveryInfoBlock } from './cms/delivery-info/cms-product-delivery-info-block';
import { NCmsProductDelivery } from './cms/delivery/cms-product-delivery.component';
import { NProductViewLayout } from './view/layout/product-view-layout.component';
interface IAttachmentFile {
    labels?: string[];
    path?: string;
    filename?: string;
}

export interface ITierPrice {
    // customer group id to apply this price for
    customer_group: number;
    // price value
    price: number;
    // min qty for this price
    qty: number;
}

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Catalog.Model.Product.Data {
        interface V1 {
            was_price?: number;
            sunitdesc?: string;
            /**
             * Tier prices
             */
            tier_price?: ITierPrice[];
        }
        interface V2 {
            /**
        /**
         * Product color
         */
            color?: number | number[];
            /**
             * Alternative image used on listing page on hover
             */
            img_alternative?: string;
            /**
             * Horizontal image that can be used on 2-col grid
             */
            img_horizontal?: string;
            /**
             * Text under product name used on listing page for banners
             */
            banner_subtitle?: string;
            /**
             * Link that banner provides
             */
            banner_url?: string;
            /**
             * Alternative image used on listing page on hover
             */
            swatch_image?: string;
            /**
             * Hide product banner on plp for trade customers (Login promo banner)
             */
            hide_for_trade?: boolean;
            media?: Type.IProductMediaItem[];
            is_delivery?: boolean;
            is_collection?: boolean;
            filter_brand?: number;

            /**
             * If consigmnet stock is used
             * and qty for it
             */
            use_consignment_stock?: 1 | 0;
            consignment_stock?: number;

            /**
             * Manual promo price label
             * @see https://tomandco.atlassian.net/browse/PTR-476
             */
            manual_promo_price_label?: number;
        }

        interface V3 {
            /**
             * Id of the product page layout (used with cms block)
             */
            product_page?: string;
            short_description?: string;
            features_block?: string;
            attachments?: IAttachmentFile[];

            /**
             * If PDP qty selector can use square-meter display
             */
            show_enhanced_qty_selector?: boolean;

            /**
             * Single piece length in meters
             */
            measurement_length?: number;

            /**
             * Single piece width in meters
             */
            measurement_width?: number;
        }
    }
}

/**
 * Colors map entry type
 */
export interface IColorMapEntry {
    color: number;
    parent: number;
}

export enum EListingLabelOptionId {
    sale = 8462,
    newIn = 8463,
    tempOos = 8464
}

export class Product<T extends IProductData = IProductData> extends CoreProduct<T> {
    /**
     * Load cms block servoce
     */
    @NgInject(ICmsBlockResource)
    private iCmsBlockResource!: ICmsBlockResource;

    /**
     * Product page layout variant
     */
    get ppLayoutVariant(): string {
        if (this.hasV3) {
            const pdpTemplate = (<IProductDataV3>this.data).product_page || (<IProductDataV3>this.parent.data)?.product_page;
            if (pdpTemplate) {
                return pdpTemplate;
            }
        }
        return 'default';
    }

    get hasColorOption(): boolean {
        const data = this.data as IProductDataV2;
        if (data && !data.isBanner && this.isConfigurable && data.options?.configurable) {
            return !!data.options?.configurable['config_colour_swatch'];
        }
        return false;
    }

    /**
     * Size guide block variant
     */
    get sizeGuideVariant(): string | undefined {
        return 'default';
    }

    /**
     * Delivery and returns guide block variant
     */
    get deliveryVariant(): string | undefined {
        return 'default';
    }

    get tradePrice(): number {
        return this.calcTradePrice() || 0;
    }

    calcTradePrice(request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>): number {
        return this.calcPrice(false, request, undefined, false, ECustomerGroup.trade) || 0;
    }

    calcTradeDiscount(request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>): number {
        return Math.round(100 * (this.calcRetailPrice(request) - this.calcTradePrice(request))) / 100;
    }

    protected get brandImage(): string | undefined {
        const filterBrand = (<IProductDataV2>this.data)?.filter_brand;
        return filterBrand ? optionImagePipe(filterBrand, 'filter_brand') : undefined;
    }

    protected get brandLabel(): string | number | undefined {
        return (<IProductDataV2>this.data)?.filter_brand
            ? optionLabelPipe((<IProductDataV2>this.data)?.filter_brand, 'filter_brand')
            : undefined;
    }

    get labelImage(): string | undefined {
        const label = this.getListingLabel();
        return label ? optionImagePipe(label, 'listing_label') : undefined;
    }

    protected get brandUrl(): string | undefined {
        return (this.brandLabel as string)?.replace(/ /g, '-');
    }

    getTierPrices(customerGroupId: number, taxMode: TaxMode = TaxMode.auto): ITierPrice[] | undefined {
        let tierPrice = this.data?.tier_price?.filter(i => i.customer_group === customerGroupId);
        if (!tierPrice) {
            return undefined;
        }
        tierPrice = sortBy(tierPrice, 'qty');
        let unitPrice = super.calcBasePrice(false, undefined, false, customerGroupId);
        return tierPrice
            .filter(i => {
                if (i.price >= unitPrice) {
                    return false;
                }
                if (i.qty === 1) {
                    unitPrice = i.price;
                }
                return true;
            })
            .map(i => {
                return { ...i, price: this.priceService.applyTax(i.price, this.data!.tax, taxMode) };
            });
    }

    hasTierPrices(customerGroupId: number): boolean {
        return !!this.getTierPrices(customerGroupId)?.find(i => i.qty > 1);
    }

    calcRetailPrice(request?: Omit<Nexus.Lib.Basket.BasketRequestData, 'id'>): number {
        const data = this.data as IProductDataV2;
        if (data) {
            const nativePrice = data._nativePrice;
            const price = data.price;
            const orgPrice = data.org_price;
            const tierPrices = data.tier_price;
            // swap prices in data for the time of calculation..
            if (nativePrice) {
                data.price = nativePrice.price;
                data.org_price = nativePrice.orgPrice;
                data.tier_price = nativePrice.tierPrices;
            }
            let retailPrice = 0;
            try {
                retailPrice = this.calcPrice(false, request, undefined, false, CustomerModel.notLoggedInGroupId) || 0;
            } finally {
                // .. swap prices back to original
                if (nativePrice) {
                    data.price = price;
                    data.org_price = orgPrice;
                    data.tier_price = tierPrices;
                }
            }
            return retailPrice;
        }
        return 0;
    }

    /**
     * Use this to set force priority attributes order
     */
    protected override getConfigurableAttributeCodesOrder() {
        return ['color'];
    }

    /**
     * Add extra things to load
     */
    protected override addDependenciesToLoader(params: TLoadDependeciesParams): void {
        super.addDependenciesToLoader(params);

        if (this.verbosity! > 2) {
            params.loader.add(
                this.iCmsBlockResource.retrieve({ bareUrl: NProductViewLayout.BLOCK_ID, variant: this.ppLayoutVariant }, params)
            );
            params.loader.add(
                this.iCmsBlockResource.retrieve({ bareUrl: NCmsProductDelivery.BLOCK_ID, variant: this.deliveryVariant }, params)
            );
            params.loader.add(this.iCmsBlockResource.retrieve({ bareUrl: NCmsProductDeliveryInfoBlock.ID }, params));
        }
    }

    /**
     * Gets final listing label value
     */
    protected override getListingLabel(): number | undefined {
        if (this.hasV2) {
            const label = (<IProductDataV2>this.data)?.listing_label;

            if (label) {
                return label;
            } else {
                if (this.isTempOutOfStock) {
                    return EListingLabelOptionId.tempOos;
                }
                if (this.isNew) {
                    return EListingLabelOptionId.newIn;
                }
                if (this.data?.org_price) {
                    return EListingLabelOptionId.sale;
                }
            }
        }

        return undefined;
    }

    protected override calcBasePrice(
        orgPrice: boolean,
        request: Nexus.Lib.Basket.BasketRequestData | undefined,
        skipGroups: boolean,
        customerGroup?: number
    ) {
        const group: number | undefined = skipGroups ? undefined : customerGroup;
        const data = this.data as IProductDataV2;
        if (!data) {
            return 0;
        }
        if (data.tier_price && !orgPrice) {
            const qty = request?.qty || 1;
            const tierPrices = typeof group === 'number' ? this.getTierPrices(group, TaxMode.skip) : [];
            const tierPrice = sortBy(
                tierPrices?.filter(tierPrice => tierPrice.qty <= qty),
                'qty'
            );
            if (tierPrice.length > 0) {
                return tierPrice[tierPrice.length - 1].price;
            }
        }
        return super.calcBasePrice(orgPrice, request, skipGroups, customerGroup);
    }
}

ProductResource.instance = Product;
